import { FormattedMessage } from 'react-intl';
import {
    PaloOverflowAction,
    PaloPositionColumn,
    PaloPositionTable,
    paloHasDescription,
} from '@synergy/components-palo';
import { ProductDetails, useLocalizedContext } from '@synergy/components';
import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { UserRole } from '@synergy/models';
import { hasRole } from '@synergy/utils';
import urlJoin from 'url-join';

import { DemandItem } from '../../../models/demand-item';
import { PaloPositionText } from '../../../../../common/components/palo/palo-position-text';
import { PaloRowProductSearch } from '../../../../../common/components/palo/palo-row-product-search';
import { Product } from '../../../../../common/models/product';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../../../store/use-appstate-selector';

export interface DemandDetailPositionsTableProps {
    readonly?: boolean;
    items: DemandItem[];
    /** Sits under the table: adding a row, adding several. */
    toolbar?: ReactNode;
    onRemove: (index: number) => void;
    onDuplicate: (index: number) => void;
    onEdit: (index: number) => void;
    /** Carries a position to another place in the list. */
    onMove: (from: number, to: number) => void;
    /** Fills a row that was added blank with the article searched for in it. */
    onPickProduct: (index: number, product: Product) => void;
    onDeleteMany: (indices: number[]) => void;
    onDuplicateMany: (indices: number[]) => void;
}

/** A row that carries no article and no name of its own waits for one. */
const isBlank = (item: DemandItem): boolean =>
    !item.product && 0 === Object.values(item.name ?? {}).filter(Boolean).length;

/**
 * The needs of a demand, in the table every document shares.
 *
 * Being no price document, it has no figures under the table (#2208). The
 * description stands under the name as in every other document; the column of
 * its own is gone (#2257).
 */
export const DemandDetailPositionsTable = (props: DemandDetailPositionsTableProps): ReactElement => {
    const {
        items,
        readonly = false,
        toolbar,
        onRemove,
        onDuplicate,
        onEdit,
        onMove,
        onPickProduct,
        onDeleteMany,
        onDuplicateMany,
    } = props;
    const user = useAppSelector(state => state.user);
    const mayFollowLink = hasRole([UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales], user);

    const columns: PaloPositionColumn<DemandItem>[] = useMemo(
        () => [
            {
                key: 'article',
                type: 'name',
                label: <FormattedMessage id="app.table.column.article" />,
                render: (item, index) =>
                    isBlank(item) && !readonly ? (
                        <PaloRowProductSearch
                            onSelected={product => onPickProduct(index, product)}
                            includeNormArticles={true}
                        />
                    ) : (
                        <>
                            <ProductDetails
                                product={item}
                                showDescription={false}
                                inlineNumber={true}
                                url={
                                    mayFollowLink && item.product
                                        ? urlJoin(ROUTES.products.product.base, item.product.id.toString())
                                        : undefined
                                }
                            />
                            <PaloPositionText
                                index={index}
                                shortDescription={item.shortDescription}
                                longDescription={item.longDescription}
                                onEdit={readonly ? undefined : () => onEdit(index)}
                            />
                        </>
                    ),
            },
            {
                key: 'orderUnit',
                type: 'classification',
                label: <FormattedMessage id="app.table.column.orderUnit" />,
                render: item => item.orderUnit,
            },
        ],
        [mayFollowLink, onPickProduct, onEdit, readonly]
    );

    const { getLocalizedString } = useLocalizedContext();
    const hasDescription = useCallback(
        (item: DemandItem) => paloHasDescription(getLocalizedString, item.shortDescription, item.longDescription),
        [getLocalizedString]
    );

    const rowActions = (item: DemandItem, index: number): PaloOverflowAction[] => {
        if (readonly) {
            return [];
        }

        return [
            { label: <FormattedMessage id="app.common.editArticle" />, onClick: () => onEdit(index) },
            ...(item.isNormArticle
                ? []
                : [
                      {
                          label: <FormattedMessage id="app.common.duplicateArticle" />,
                          onClick: () => onDuplicate(index),
                      },
                  ]),
            {
                label: <FormattedMessage id="app.imports.detail.deleteArticle" />,
                danger: true,
                onClick: () => onRemove(index),
            },
        ];
    };

    return (
        <PaloPositionTable
            items={items}
            columns={columns}
            getRowKey={(item, index) => `${item.id ?? 'new'}-${index}`}
            hasDescription={hasDescription}
            readonly={readonly}
            onMove={onMove}
            rowActions={rowActions}
            onDeleteMany={onDeleteMany}
            onDuplicateMany={onDuplicateMany}
            toolbar={toolbar}
        />
    );
};
